import i18n from 'i18next';
import { batch } from 'react-redux';
import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_JOIN_IN_PROGRESS, ENDPOINT_MESSAGE_RECEIVED, NON_PARTICIPANT_MESSAGE_RECEIVED } from '../base/conference/actionTypes';
import { redirect } from '../base/conference/actions';
import { disconnectAny, setPreferVisitor } from '../base/connection/actions.any';
import { openDialog } from '../base/dialog/actions';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import { LOCAL_PARTICIPANT_RAISE_HAND } from '../base/participants/actionTypes';
import { raiseHand } from '../base/participants/actions';
import { getLocalParticipant, getParticipantById } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { playSound, registerSound } from '../base/sounds';
import { getDisabledSounds } from '../base/sounds/functions.any';
import { BUTTON_TYPES } from '../base/ui/constants.any';
import { selectParticipantInLargeVideo } from '../large-video/actions';
import { INCOMING_USER_REQ_SOUND_ID } from '../lobby/components/constants';
import { INCOMING_USER_KNOCKING_SOUND_FILE } from '../lobby/components/sounds';
import { hideNotification, showNotification } from '../notifications/actions';
import { NOTIFICATION_ICON, NOTIFICATION_TIMEOUT_TYPE, VISITORS_PROMOTION_NOTIFICATION_ID, VISITOR_ASKED_TO_JOIN_NOTIFICATION_ID } from '../notifications/constants';
import { open as openParticipantsPane } from '../participants-pane/actions';
import { I_AM_VISITOR_MODE } from './actionTypes';
import { abortRoleTransition, approveRequest, clearPromotionRequest, denyRequest, promotionRequestReceived, reconnectAfterRoleChange, setRoleTransition, setAutoPromoteVisitors, setIAmChatSuspended, setIAmHandBlocked, setVisitorChatSuspended, setVisitorsRaiseHandDisabled, setVisitorDemoteActor, setVisitorHandBlocked, setVisitorsEnabled, setVisitorsRoster, setVisitorsSupported, updateVisitorsCount, visitorJoined, visitorLeft } from './actions';
import JoinMeetingDialog from './components/web/JoinMeetingDialog';
import { VISITOR_MODE_DISABLED } from './constants';
import { verr, vlog } from './debug';
import { getPromotionRequests, iAmVisitor, isLocalHandBlocked } from './functions';
import logger from './logger';
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    var _a, _b, _c, _d;
    switch (action.type) {
        case CONFERENCE_JOIN_IN_PROGRESS: {
            const { conference } = action;
            conference.on(MHConferenceEvents.PROPERTIES_CHANGED, (properties) => {
                const visitorCount = Number(properties === null || properties === void 0 ? void 0 : properties['visitor-count']);
                if (!isNaN(visitorCount) && getState()['features/visitors'].count !== visitorCount) {
                    dispatch(updateVisitorsCount(visitorCount));
                }
            });
            break;
        }
        case CONFERENCE_JOINED: {
            const { conference } = action;
            vlog('7. CONFERENCE_JOINED', {
                iAmVisitor: getState()['features/visitors'].iAmVisitor,
                visitorsSupported: (_a = conference.isVisitorsSupported) === null || _a === void 0 ? void 0 : _a.call(conference)
            });
            if (getState()['features/visitors'].iAmVisitor) {
                const { demoteActorDisplayName } = getState()['features/visitors'];
                const { iAmSipGateway, visitors } = getState()['features/base/config'] || {};
                const { showJoinMeetingDialog = true } = visitors || {};
                if (demoteActorDisplayName) {
                    // A moderator moved this participant into visitor mode - tell them who did it.
                    const notificationParams = {
                        titleKey: 'visitors.notification.title',
                        descriptionKey: 'visitors.notification.demoteDescription',
                        descriptionArguments: {
                            actor: demoteActorDisplayName
                        }
                    };
                    batch(() => {
                        dispatch(showNotification(notificationParams, NOTIFICATION_TIMEOUT_TYPE.STICKY));
                        dispatch(setVisitorDemoteActor(undefined));
                    });
                }
                else if (showJoinMeetingDialog && !iAmSipGateway) {
                    // Explain to the visitor that they are a viewer and how to ask to join (raise hand).
                    dispatch(openDialog(JoinMeetingDialog));
                }
            }
            else {
                dispatch(setVisitorsSupported(conference.isVisitorsSupported()));
                conference.on(MHConferenceEvents.VISITORS_SUPPORTED_CHANGED, (value) => {
                    dispatch(setVisitorsSupported(value));
                });
            }
            conference.on(MHConferenceEvents.VISITORS_MESSAGE, (msg) => {
                vlog('2. VISITORS_MESSAGE received (target side)', msg);
                if (msg.action === 'suspend-chat') {
                    // Server-confirmed per-visitor chat suspension, broadcast to moderators.
                    if (typeof msg.vid === 'string' && msg.vid) {
                        dispatch(setVisitorChatSuspended(msg.vid, msg.on === true));
                    }
                }
                else if (msg.action === 'visitors-roster') {
                    // Snapshot of everyone on the visitor nodes, sent to a joining
                    // moderator. cjson encodes an empty roster as {}, hence the
                    // array guard. The chat-suspend flags ride on the entries so a
                    // late-joining moderator learns the suspend state too.
                    const visitors = Array.isArray(msg.visitors) ? msg.visitors : [];
                    dispatch(setVisitorsRoster(visitors));
                    for (const visitor of visitors) {
                        if (visitor.chatSuspended) {
                            dispatch(setVisitorChatSuspended(visitor.vid, true));
                        }
                    }
                }
                else if (msg.action === 'visitor-joined') {
                    if (msg.visitor && typeof msg.visitor.vid === 'string') {
                        dispatch(visitorJoined(msg.visitor));
                    }
                }
                else if (msg.action === 'visitor-left') {
                    if (typeof msg.vid === 'string' && msg.vid) {
                        dispatch(visitorLeft(msg.vid));
                    }
                }
                else if (msg.action === 'block-raise-hand') {
                    // Server-confirmed raise-hand block for one visitor.
                    if (typeof msg.vid === 'string' && msg.vid) {
                        dispatch(setVisitorHandBlocked(msg.vid, msg.on === true));
                    }
                }
                else if (msg.action === 'demote-request') {
                    // we need it before the disconnectAny
                    const participantById = getParticipantById(getState, msg.actor);
                    const localParticipant = getLocalParticipant(getState);
                    vlog('3. demote-request: matching against local participant', {
                        msgId: msg.id,
                        localId: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id,
                        isForMe: Boolean(localParticipant && localParticipant.id === msg.id)
                    });
                    if (localParticipant && localParticipant.id === msg.id) {
                        // Handle demote: leave the current conference, flag the visitor preference and
                        // rejoin so jicofo redirects us to a visitor node. A full connect() (not a bare
                        // connectVisitor()) is required here because the redirect is only triggered once a
                        // conference request is sent during the (re)join. The loading screen goes up
                        // first and stays until the viewer room is joined.
                        vlog('4. demote-request is for me: disconnectAny -> setPreferVisitor(true) -> connect()');
                        dispatch(setRoleTransition({ kind: 'to-viewer' }));
                        dispatch(disconnectAny(true))
                            .then(() => {
                            vlog('4a. disconnectAny resolved; setPreferVisitor(true)');
                            dispatch(setPreferVisitor(true));
                            // we need to set the name, so we can use it later in the notification
                            if (participantById) {
                                dispatch(setVisitorDemoteActor(participantById.name));
                            }
                            vlog('4b. dispatching connect() (re-init to trigger redirect)');
                            return dispatch(reconnectAfterRoleChange());
                        })
                            .then(() => vlog('4c. connect() chain resolved (redirect handles the rest)'))
                            .catch((e) => {
                            verr('demote chain failed', e);
                            dispatch(abortRoleTransition());
                        });
                    }
                }
                else if (msg.action === 'promotion-request') {
                    const request = {
                        from: msg.from,
                        nick: msg.nick
                    };
                    if (msg.on) {
                        dispatch(promotionRequestReceived(request));
                        _playIncomingVisitorsKnockSound({ dispatch,
                            getState });
                    }
                    else {
                        dispatch(clearPromotionRequest(request));
                    }
                    _handlePromotionNotification({
                        dispatch,
                        getState
                    });
                }
                else if (typeof msg.name === 'string') {
                    // The webinar controls (move/role/hide/kick/mute requests and the
                    // backstage/main rosters) ride the same 'visitors' json channel but
                    // are keyed on 'name' rather than 'action', and are handled by the
                    // webinar middleware. They are not unknown, so do not log them.
                    vlog('visitors-channel message for the webinar middleware', msg);
                }
                else {
                    // add it later on
                    // logger.error('Unknown action:', msg.action);
                }
            });
            conference.on(MHConferenceEvents.VISITORS_REJECTION, () => {
                dispatch(raiseHand(false));
                dispatch(showNotification({
                    titleKey: 'visitors.notification.title',
                    descriptionKey: 'visitors.notification.requestDenied'
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            });
            // Track backend room visitor metadata (armed state + active toggle mode). This is the source
            // of truth for whether visitor mode is on for the room, and it settles any pending moderator
            // toggle. It does not, on its own, mark the local user as a visitor - that still comes from the
            // connection redirect / role.
            conference.on(MHConferenceEvents.METADATA_UPDATED, (metadata) => {
                _handleVisitorsMetadata(dispatch, metadata);
            });
            // Process any visitor metadata already present at join time.
            _handleVisitorsMetadata(dispatch, (_d = (_c = (_b = conference.getMetadataHandler) === null || _b === void 0 ? void 0 : _b.call(conference)) === null || _c === void 0 ? void 0 : _c.getMetadata) === null || _d === void 0 ? void 0 : _d.call(_c));
            _finishRoleTransition(dispatch, getState);
            break;
        }
        case CONFERENCE_FAILED: {
            dispatch(abortRoleTransition());
            break;
        }
        case ENDPOINT_MESSAGE_RECEIVED: {
            const { data } = action;
            // Another moderator answered (admit, admit-all or reject): drop the
            // request here too so no moderator keeps a stale knock.
            if ((data === null || data === void 0 ? void 0 : data.action) === 'promotion-response') {
                const ids = Array.isArray(data.ids) ? data.ids : [data.id];
                getPromotionRequests(getState())
                    .filter(r => ids.includes(r.from))
                    .forEach(r => dispatch(clearPromotionRequest(r)));
                _handlePromotionNotification({
                    dispatch,
                    getState
                });
            }
            break;
        }
        case NON_PARTICIPANT_MESSAGE_RECEIVED: {
            // Visitor mode was turned off for the whole room. This only ever arrives
            // from the room itself (prosody relays it to each vnode and stamps
            // webinarModerator), so a visitor cannot push their peers back into the
            // meeting by sending it from their own occupant jid.
            const { id, json } = action;
            if ((json === null || json === void 0 ? void 0 : json.type) === 'visitors'
                && json.action === VISITOR_MODE_DISABLED
                && json.webinarModerator === true
                && iAmVisitor(getState())) {
                vlog('visitor mode disabled for the room, rejoining the main room as a guest');
                batch(() => {
                    dispatch(setVisitorsEnabled(false));
                    dispatch(showNotification({
                        titleKey: 'visitors.notification.title',
                        descriptionKey: 'visitors.notification.visitorModeOffDescription'
                    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                });
                // The same path a moderator-approved promotion takes: restore the main
                // room config, drop the visitor flag and reconnect. No one-time
                // username is involved - the room simply has no allow-list any more.
                dispatch(redirect(undefined, undefined, undefined));
            }
            else if ((json === null || json === void 0 ? void 0 : json.type) === 'visitors' && !id && iAmVisitor(getState())) {
                // Vnode notices come from the bare room JID, not an occupant.
                if (json.action === 'raise-hand-blocked') {
                    const on = json.on === true;
                    if (json.reason === 'room') {
                        const { iAmHandBlocked, raiseHandDisabled } = getState()['features/visitors'];
                        batch(() => {
                            dispatch(setVisitorsRaiseHandDisabled(on));
                            if (on) {
                                dispatch(raiseHand(false));
                            }
                            if (on !== Boolean(raiseHandDisabled) && (on || !iAmHandBlocked)) {
                                dispatch(showNotification({
                                    titleKey: 'visitors.notification.title',
                                    descriptionKey: on
                                        ? 'visitors.notification.raiseHandDisabledByRoom'
                                        : 'visitors.notification.raiseHandUnblocked'
                                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                            }
                        });
                    }
                    else {
                        batch(() => {
                            dispatch(setIAmHandBlocked(on));
                            dispatch(showNotification({
                                titleKey: 'visitors.notification.title',
                                descriptionKey: on
                                    ? 'visitors.notification.raiseHandBlocked'
                                    : getState()['features/visitors'].raiseHandDisabled
                                        ? 'visitors.notification.raiseHandDisabledByRoom'
                                        : 'visitors.notification.raiseHandUnblocked'
                            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                        });
                    }
                }
                else if (json.action === 'chat-suspended') {
                    const on = json.on === true;
                    batch(() => {
                        dispatch(setIAmChatSuspended(on));
                        dispatch(showNotification({
                            titleKey: 'visitors.notification.title',
                            descriptionKey: on
                                ? 'visitors.notification.chatSuspended'
                                : 'visitors.notification.chatResumed'
                        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                    });
                }
            }
            break;
        }
        case LOCAL_PARTICIPANT_RAISE_HAND: {
            // Both individual and room-wide blocks apply to shortcuts/API calls.
            if (action.raisedHand && isLocalHandBlocked(getState())) {
                dispatch(showNotification({
                    titleKey: 'visitors.notification.title',
                    descriptionKey: getState()['features/visitors'].raiseHandDisabled
                        ? 'visitors.notification.raiseHandDisabledByRoom'
                        : 'visitors.notification.raiseHandBlocked'
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                return;
            }
            break;
        }
        case I_AM_VISITOR_MODE: {
            // Apply the flag first so selectors below see the new visitor state.
            const result = next(action);
            // When the local user stops being a visitor (e.g. they were promoted into the main meeting),
            // clear the sticky "your request to join was sent" notification.
            if (!action.enabled) {
                dispatch(hideNotification(VISITOR_ASKED_TO_JOIN_NOTIFICATION_ID));
            }
            if (typeof APP !== 'undefined') {
                const VideoLayout = require('../video-layout/VideoLayout').default;
                VideoLayout.refreshLayout();
                dispatch(selectParticipantInLargeVideo());
            }
            return result;
        }
    }
    return next(action);
});
const MEETING_ROLE_LABEL = {
    moderator: 'webinar.role.moderator',
    panelist: 'webinar.role.panelist',
    participant: 'webinar.role.participant',
    presenter: 'webinar.role.presenter'
};
/**
 * Closes the loading screen once the new room is joined, and says what the
 * user landed as. Moving to viewer mode already has its own notice (who
 * moved them, and the viewer dialog), so only the way back to the meeting
 * gets one here.
 *
 * @param {Function} dispatch - The redux dispatch function.
 * @param {Function} getState - The redux getState function.
 * @returns {void}
 */
function _finishRoleTransition(dispatch, getState) {
    var _a;
    const transition = getState()['features/visitors'].roleTransition;
    if (!transition) {
        return;
    }
    dispatch(setRoleTransition(undefined));
    if (transition.kind !== 'to-meeting') {
        return;
    }
    if (iAmVisitor(getState())) {
        dispatch(showNotification({
            titleKey: 'visitors.notification.title',
            descriptionKey: 'visitors.transition.stillViewer'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const localRole = (_a = getLocalParticipant(getState())) === null || _a === void 0 ? void 0 : _a.role;
    const elevated = localRole === 'moderator' || localRole === 'panelist' || localRole === 'presenter';
    const role = elevated ? localRole : transition.role;
    const labelKey = role ? MEETING_ROLE_LABEL[role] : undefined;
    dispatch(showNotification({
        titleKey: 'visitors.notification.title',
        descriptionKey: labelKey
            ? 'visitors.transition.nowInMeeting'
            : 'visitors.transition.nowInMeetingPlain',
        descriptionArguments: labelKey ? { role: i18n.t(labelKey) } : undefined
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
/**
 * Reads visitor mode state out of room metadata and stores it in redux. Tolerant of both the
 * {@code visitors: { enabled, mode, ... }} shape we write from the toggle and a flat
 * {@code visitorsEnabled} shape a backend may broadcast.
 *
 * @param {Function} dispatch - The redux dispatch function.
 * @param {Object} metadata - The full room metadata object.
 * @returns {void}
 */
function _handleVisitorsMetadata(dispatch, metadata) {
    if (!metadata) {
        return;
    }
    const visitors = metadata.visitors;
    let enabled;
    let mode;
    let updatedBy;
    let updatedAt;
    if (visitors && typeof visitors === 'object') {
        if (typeof visitors.enabled === 'boolean') {
            enabled = visitors.enabled;
        }
        mode = visitors.mode;
        updatedBy = visitors.updatedBy;
        updatedAt = visitors.updatedAt;
        // Auto-promote is tracked independently of the enabled flag (and before
        // the early return below), so a room that sets it before ever arming
        // visitor mode still reflects server truth.
        dispatch(setAutoPromoteVisitors(typeof visitors.autoPromote === 'boolean' ? visitors.autoPromote : undefined));
        // Room-wide raise-hand block for viewers, same pattern as autoPromote.
        dispatch(setVisitorsRaiseHandDisabled(typeof visitors.raiseHandDisabled === 'boolean' ? visitors.raiseHandDisabled : undefined));
    }
    if (typeof enabled !== 'boolean' && typeof metadata.visitorsEnabled === 'boolean') {
        enabled = metadata.visitorsEnabled;
    }
    if (typeof enabled !== 'boolean') {
        return;
    }
    vlog('visitors metadata received', {
        enabled,
        mode,
        updatedBy
    });
    dispatch(setVisitorsEnabled(enabled, mode, updatedBy, updatedAt));
}
/**
 * Function to handle the promotion notification.
 *
 * @param {Object} store - The Redux store.
 * @returns {void}
 */
function _handlePromotionNotification({ dispatch, getState }) {
    // Web renders visitor promotion requests in the same waiting-participant
    // dialog used for lobby guests whenever the participants pane is closed.
    // Keep the existing notification behavior on native, where that web dialog
    // is not available.
    if (navigator.product !== 'ReactNative') {
        dispatch(hideNotification(VISITORS_PROMOTION_NOTIFICATION_ID));
        return;
    }
    const requests = getPromotionRequests(getState());
    if (requests.length === 0) {
        dispatch(hideNotification(VISITORS_PROMOTION_NOTIFICATION_ID));
        return;
    }
    let notificationTitle;
    let customActionNameKey;
    let customActionHandler;
    let customActionType;
    let descriptionKey;
    let icon;
    if (requests.length === 1) {
        const firstRequest = requests[0];
        descriptionKey = 'notify.participantWantsToJoin';
        notificationTitle = firstRequest.nick;
        icon = NOTIFICATION_ICON.PARTICIPANT;
        customActionNameKey = ['participantsPane.actions.admit', 'participantsPane.actions.reject'];
        customActionType = [BUTTON_TYPES.PRIMARY, BUTTON_TYPES.DESTRUCTIVE];
        customActionHandler = [() => batch(() => {
                dispatch(hideNotification(VISITORS_PROMOTION_NOTIFICATION_ID));
                dispatch(approveRequest(firstRequest));
            }),
            () => batch(() => {
                dispatch(hideNotification(VISITORS_PROMOTION_NOTIFICATION_ID));
                dispatch(denyRequest(firstRequest));
            })];
    }
    else {
        descriptionKey = 'notify.participantsWantToJoin';
        notificationTitle = i18n.t('notify.waitingParticipants', {
            waitingParticipants: requests.length
        });
        icon = NOTIFICATION_ICON.PARTICIPANTS;
        customActionNameKey = ['notify.viewVisitors'];
        customActionType = [BUTTON_TYPES.PRIMARY];
        customActionHandler = [() => batch(() => {
                dispatch(hideNotification(VISITORS_PROMOTION_NOTIFICATION_ID));
                dispatch(openParticipantsPane());
            })];
    }
    dispatch(showNotification({
        title: notificationTitle,
        descriptionKey,
        uid: VISITORS_PROMOTION_NOTIFICATION_ID,
        customActionNameKey,
        customActionType,
        customActionHandler,
        icon
    }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
}
/**
 * Plays the incoming request sound for visitors promotion requests.
 *
 * @param {Object} store - Redux helpers.
 * @returns {void}
 */
function _playIncomingVisitorsKnockSound({ dispatch, getState }) {
    Promise.resolve(getDisabledSounds(getState()))
        .then(disabledSounds => {
        const state = getState();
        const { disabledLobbySound } = state['features/lobby'] || {};
        const hasSoundRegistry = Boolean(disabledSounds && typeof disabledSounds.includes === 'function');
        const disabledBySoundRegistry = hasSoundRegistry
            ? disabledSounds.includes(INCOMING_USER_REQ_SOUND_ID)
            : false;
        const shouldPlay = !(disabledBySoundRegistry || Boolean(disabledLobbySound));
        if (shouldPlay) {
            // Re-register before playback to keep this path resilient across room switches.
            dispatch(registerSound(INCOMING_USER_REQ_SOUND_ID, INCOMING_USER_KNOCKING_SOUND_FILE));
            dispatch(playSound(INCOMING_USER_REQ_SOUND_ID));
        }
    })
        .catch(err => logger.error('Failed to evaluate disabled sounds for visitors knock', err));
}
